import { useEffect, useState } from "react";
import { fetchSirarFilterTree } from "../api/logReviewApi";
import { useAuthStore } from "../stores/authStore";
import type { LogReviewFilterDivision } from "../app/types/logReview";

export interface UseSirarFilterTreeResult {
  tree: LogReviewFilterDivision[];
  loading: boolean;
  error: string | null;
}

/**
 * Loads the Division → Application → Category cascade for the Log Review top
 * filter bar (shared by the main dashboard, Reviewer Insights and CISO Log
 * Review), scoped to the signed-in user's organization. Re-fetched when that
 * organization changes and aborted on unmount.
 */
export const useSirarFilterTree = (): UseSirarFilterTreeResult => {
  const organizationId = useAuthStore((s) => s.user?.orgId ?? null);
  const [tree, setTree] = useState<LogReviewFilterDivision[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchSirarFilterTree(organizationId, controller.signal)
      .then((divisions) => {
        if (controller.signal.aborted) return;
        setTree(divisions);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : "Failed to load filters");
        setLoading(false);
      });
    return () => controller.abort();
  }, [organizationId]);

  return { tree, loading, error };
};
